<script setup lang="ts">
import { useEventListener, whenever } from "@vueuse/core"
import SignaturePad from "signature_pad"
import { onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue"

const props = withDefaults(
    defineProps<{
        modelValue?: string | null
        width?: string
        height?: string
    }>(),
    {
        modelValue: null,
        width: "400px",
        height: "200px",
    },
)

const emit = defineEmits<{
    (e: "update:modelValue", value: string): void
    (e: "empty", isEmpty: boolean): void
}>()

const canvas = useTemplateRef("canvas")
const mode = ref<"draw" | "erase">("draw")
const data = ref<string | null>(props.modelValue)
const isEmpty = ref(true)
let signaturePad: SignaturePad
let loadedData: string

useEventListener("resize", resizeCanvas)

watch(isEmpty, value => {
    emit("empty", value)
})

whenever(
    () => props.modelValue,
    () => {
        if (!props.modelValue) {
            return
        }
        loadData(props.modelValue)
    },
)

onMounted(() => {
    signaturePad = new SignaturePad(canvas.value!, {
        minWidth: 0.2,
        maxWidth: 1,
    })
    signaturePad.addEventListener("endStroke", updateData)

    if (data.value) {
        loadData(data.value, true)
    }

    drawMode()
    resizeCanvas()

    isEmpty.value = signaturePad.isEmpty()
})

onUnmounted(() => {
    signaturePad.off()
})

function resizeCanvas() {
    // correct pixel ratio causes incorrect drawing positions, so lines do not appear where they should
    const ratio = 1 // Math.max(window.devicePixelRatio || 1, 1)
    canvas.value!.width = canvas.value!.offsetWidth * ratio
    canvas.value!.height = canvas.value!.offsetHeight * ratio
    canvas.value!.getContext("2d")?.scale(ratio, ratio)

    // browser clears canvas on resize, so we need to redraw
    signaturePad.fromData(signaturePad.toData())
}

function clear() {
    signaturePad.clear()
    updateData()
}

function drawMode() {
    const ctx = canvas.value!.getContext("2d")
    ctx!.globalCompositeOperation = "source-over" // default value
    mode.value = "draw"
}

function updateData() {
    loadedData = signaturePad.toDataURL("image/png")
    data.value = loadedData

    emit("update:modelValue", data.value)
    isEmpty.value = signaturePad.isEmpty()
}

async function loadData(newData: string, force = false) {
    if (loadedData !== newData || force) {
        await signaturePad.fromDataURL(newData)
        loadedData = newData
        data.value = newData
    }
}

const uploadInput = useTemplateRef("uploadInput")
function upload() {
    uploadInput.value!.click()
}
function uploadChanged() {
    const file = uploadInput.value?.files?.[0]
    if (!file) {
        return
    }

    const reader = new FileReader()
    reader.onload = function (e) {
        const dataUrl = e.target?.result as string
        const img = new Image()

        img.onload = async () => {
            // Create a temporary canvas to handle resizing
            const tempCanvas = document.createElement("canvas")
            const tempCtx = tempCanvas.getContext("2d")!

            // Set temp canvas size to match our main canvas
            tempCanvas.width = canvas.value!.width
            tempCanvas.height = canvas.value!.height

            // Calculate scaling to maintain aspect ratio
            const scale = Math.min(tempCanvas.width / img.width, tempCanvas.height / img.height)

            // Calculate centered position
            const x = (tempCanvas.width - img.width * scale) / 2
            const y = (tempCanvas.height - img.height * scale) / 2

            // Draw image onto temp canvas with correct scaling and position
            tempCtx.drawImage(img, x, y, img.width * scale, img.height * scale)

            // Convert to data URL and load into signature pad
            const scaledDataUrl = tempCanvas.toDataURL("image/png")
            await loadData(scaledDataUrl, true)
            updateData()
        }

        img.src = dataUrl
    }

    reader.readAsDataURL(file)
}
</script>

<template>
    <div class="signature-pad">
        <input type="file" accept="image/jpeg,image/png" class="d-none" ref="uploadInput" @change="uploadChanged" />
        <canvas ref="canvas" :style="{ width, height }"></canvas>

        <div>
            <button @click="clear" class="btn btn-danger" type="button">
                <i class="fa fa-file"></i>
                {{ $t("@app:components.signature_pad.reset") }}
            </button>
            <button @click="upload" class="btn btn-primary" type="button">
                <i class="fa fa-fw fa-upload"></i>
                {{ $t("@app:components.signature_pad.upload") }}
            </button>
        </div>
    </div>
</template>

<style scoped>
canvas {
    background-color: var(--light);
    border-radius: 5px;
}
</style>
